// @ts-nocheck
import React, { useState, useEffect } from "react";
import { ProcessedModelRecord, Asset, AssetGroup, AdditionalDataPoint, createProcessedModel, updateProcessedModelRecord } from "./utils";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { HexColorPicker, HexColorInput } from "react-colorful";

import Select from "../ReusableComponents/Select";
interface ProcessedModelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  assets: Asset[];
  assetGroup: AssetGroup;
  editingModel?: ProcessedModelRecord | null;
}

const ProcessedModelModal: React.FC<ProcessedModelModalProps> = ({
  isOpen,
  onClose,
  onSave,
  assets,
  assetGroup,
  editingModel,
}) => {
  const user = useSelector((state: RootState) => state.user);
  const isEditMode = !!editingModel;

  // Form state
  const [assetId, setAssetId] = useState("");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [customProcessedModelShort, setCustomProcessedModelShort] = useState("");
  const [owner, setOwner] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [additionalDataPoints, setAdditionalDataPoints] = useState<AdditionalDataPoint[]>([]);
  const [newDataPointName, setNewDataPointName] = useState("");
  const [newDataPointParameterId, setNewDataPointParameterId] = useState("");
  const [newDataPointSource, setNewDataPointSource] = useState("");
  const [newDataPointSchema, setNewDataPointSchema] = useState("");
  const [newDataPointLookbackDays, setNewDataPointLookbackDays] = useState("");
  const [newDataPointColor, setNewDataPointColor] = useState("#3b82f6");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form with editing data
  useEffect(() => {
    if (editingModel) {
      setAssetId(editingModel.assetId);
      setLabel(editingModel.label);
      setDescription(editingModel.description || "");
      setCustomProcessedModelShort(editingModel.routeShort);
      setOwner(editingModel.owner);
      setOrganizationId(editingModel.organizationId || "");
      setAdditionalDataPoints(editingModel.additionalDataPoints || []);
    } else {
      resetForm();
    }
  }, [editingModel]);

  const resetForm = () => {
    setAssetId("");
    setLabel("");
    setDescription("");
    setCustomProcessedModelShort("");
    setOwner("");
    setOrganizationId("");
    setAdditionalDataPoints([]);
    setNewDataPointName("");
    setNewDataPointParameterId("");
    setNewDataPointSource("");
    setNewDataPointSchema("");
    setNewDataPointLookbackDays("");
    setNewDataPointColor("#3b82f6");
    setShowColorPicker(false);
    setError(null);
  };

  const handleAssetChange = (selectedAssetId: string) => {
    const selectedAsset = assets.find((a) => a.id === selectedAssetId);
    if (selectedAsset) {
      setAssetId(selectedAssetId);
      setOwner(selectedAsset.owner);
      setOrganizationId(selectedAsset.organizationId || "");
    }
  };

  const handleAddDataPoint = () => {
    if (newDataPointName.trim()) {
      const newDataPoint: AdditionalDataPoint = {
        id: `dp-${Date.now()}`,
        name: newDataPointName.trim(),
        parameterId: newDataPointParameterId ? parseInt(newDataPointParameterId) : undefined,
        source: newDataPointSource.trim() || undefined,
        schema: newDataPointSchema.trim() || undefined,
        lookbackDays: newDataPointLookbackDays ? parseInt(newDataPointLookbackDays) : undefined,
        color: newDataPointColor || undefined,
      };
      setAdditionalDataPoints([...additionalDataPoints, newDataPoint]);
      setNewDataPointName("");
      setNewDataPointParameterId("");
      setNewDataPointSource("");
      setNewDataPointSchema("");
      setNewDataPointLookbackDays("");
      setNewDataPointColor("#3b82f6");
      setShowColorPicker(false);
    }
  };

  const handleRemoveDataPoint = (id: string) => {
    setAdditionalDataPoints(additionalDataPoints.filter((dp) => dp.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!assetId || !label.trim() || !customProcessedModelShort.trim() || !owner.trim()) {
      setError("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);

      if (isEditMode) {
        // Update existing model
        const updateInput = {
          id: editingModel.id,
          label: label.trim(),
          description: description.trim(),
          owner: owner.trim(),
          organizationId: organizationId.trim() || undefined,
          additionalDataPoints: additionalDataPoints.length > 0 ? additionalDataPoints : undefined,
        };
        console.log("=== UPDATE INPUT ===", JSON.stringify(updateInput, null, 2));
        await updateProcessedModelRecord(updateInput);
      } else {
        // Create new model
        const createInput = {
          assetId,
          label: label.trim(),
          description: description.trim(),
          routeShort: customProcessedModelShort.trim(),
          owner: owner.trim(),
          cognitoIdentityId: user.permissionDetails?.cognitoIdentityId || "",
          organizationId: organizationId.trim() || undefined,
          additionalDataPoints: additionalDataPoints.length > 0 ? additionalDataPoints : undefined,
        };
        console.log("=== CREATE INPUT ===", JSON.stringify(createInput, null, 2));
        await createProcessedModel(createInput);
      }

      onSave();
      handleClose();
    } catch (err) {
      console.error("Error saving ProcessedModel:", err);
      setError(`Failed to ${isEditMode ? "update" : "create"} ProcessedModel`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90dvh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditMode ? "Edit ProcessedModel" : "Add New ProcessedModel"}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Asset Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Asset <span className="text-red-500">*</span>
            </label>
            <Select
              value={assetId}
              onChange={(e) => handleAssetChange(e.target.value)}
              disabled={isEditMode}
              required
              containerClassName="w-full"
            >
              <option value="">Select an asset...</option>
              {assets.map((asset) => (
                <option key={asset.id} value={asset.id}>
                  {asset.name} - {assetGroup.name}
                  {asset.organizationId ? ` - ${asset.organizationId}` : ""}
                </option>
              ))}
            </Select>
          </div>

          {/* Label */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Label <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="e.g., Turbine Flow"
              required
            />
          </div>

          {/* Description */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              rows={3}
              placeholder="Optional description"
            />
          </div>

          {/* Custom ProcessedModel Short */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Route Short <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={customProcessedModelShort}
              onChange={(e) => setCustomProcessedModelShort(e.target.value)}
              disabled={isEditMode}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 font-mono text-sm"
              placeholder="e.g., turbineflows"
              required
            />
            {isEditMode && (
              <p className="text-xs text-gray-500 mt-1">
                Route Short cannot be edited
              </p>
            )}
          </div>

          {/* Route Long (Edit mode only) */}
          {isEditMode && editingModel && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Route Long
              </label>
              <input
                type="text"
                value={editingModel.routeLong}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 font-mono text-sm text-gray-600"
              />
              <p className="text-xs text-gray-500 mt-1">
                Route Long cannot be edited
              </p>
            </div>
          )}

          {/* Owner */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Owner <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Owner email or ID"
              required
            />
          </div>

          {/* Organization ID */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organization ID
            </label>
            <input
              type="text"
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Optional organization ID"
            />
          </div>

          {/* Additional Data Points */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Data Points
            </label>

            {/* List existing data points */}
            {additionalDataPoints.length > 0 && (
              <div className="mb-3 space-y-2">
                {additionalDataPoints.map((dp) => (
                  <div
                    key={dp.id}
                    className="p-3 bg-gray-50 rounded border border-gray-200"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-gray-900">{dp.name}</div>
                          {dp.color && (
                            <div
                              className="w-4 h-4 rounded border border-gray-300"
                              style={{ backgroundColor: dp.color }}
                              title={dp.color}
                            />
                          )}
                        </div>
                        <div className="mt-1 space-y-1">
                          {dp.parameterId && (
                            <div className="text-xs text-gray-600">Parameter ID: {dp.parameterId}</div>
                          )}
                          {dp.source && (
                            <div className="text-xs text-gray-600">Source: {dp.source}</div>
                          )}
                          {dp.schema && (
                            <div className="text-xs text-gray-600">Schema: {dp.schema}</div>
                          )}
                          {dp.lookbackDays && (
                            <div className="text-xs text-gray-600">Lookback Days: {dp.lookbackDays}</div>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveDataPoint(dp.id)}
                        className="ml-2 text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add new data point form */}
            <div className="border border-gray-200 rounded-lg p-3 bg-white">
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <input
                    type="text"
                    value={newDataPointName}
                    onChange={(e) => setNewDataPointName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                    placeholder="Name *"
                  />
                </div>
                <div>
                  <input
                    type="number"
                    value={newDataPointParameterId}
                    onChange={(e) => setNewDataPointParameterId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                    placeholder="Parameter ID"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    value={newDataPointSource}
                    onChange={(e) => setNewDataPointSource(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                    placeholder="Source"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    value={newDataPointSchema}
                    onChange={(e) => setNewDataPointSchema(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                    placeholder="Schema"
                  />
                </div>
                <div>
                  <input
                    type="number"
                    value={newDataPointLookbackDays}
                    onChange={(e) => setNewDataPointLookbackDays(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                    placeholder="Lookback Days"
                  />
                </div>
                <div className="relative">
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <HexColorInput
                        color={newDataPointColor}
                        onChange={setNewDataPointColor}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm font-mono"
                        placeholder="#3b82f6"
                        prefixed
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowColorPicker(!showColorPicker)}
                      className="w-10 h-10 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                      style={{ backgroundColor: newDataPointColor }}
                      title="Pick color"
                    />
                  </div>
                  {showColorPicker && (
                    <div className="absolute top-12 right-0 z-10 p-3 bg-white rounded-lg shadow-xl border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-medium text-gray-700">Pick a color</span>
                        <button
                          type="button"
                          onClick={() => setShowColorPicker(false)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          ✕
                        </button>
                      </div>
                      <HexColorPicker color={newDataPointColor} onChange={setNewDataPointColor} />
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={handleAddDataPoint}
                className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium"
              >
                Add Data Point
              </button>
            </div>
          </div>

          {/* Fixed Fields Info */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <p className="text-xs text-blue-700">
              <strong>Note:</strong> Model Type will be set to "PROCESSED" and Display Type will be set to "scatter" automatically.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="eai-btn-grn px-4 py-2 disabled:opacity-50"
            >
              {loading ? "Saving..." : isEditMode ? "Update Model" : "Create Model"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProcessedModelModal;
